import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  fetchTenantConfig,
  getCurrentTenantConfig,
  getSubdomain,
  clearTenantCache
} from '../services/tenant.service';
import { DEFAULT_BRAND_COLOR, deriveBrandInk } from '../utils/colorUtils';

const TenantContext = createContext(null);

// Light-theme fallbacks. Dark mode is a controlled palette owned by
// TenantThemeProvider and is never tenant-configurable.
const defaultTheme = {
  primaryColor: DEFAULT_BRAND_COLOR,
  secondaryColor: deriveBrandInk(DEFAULT_BRAND_COLOR),
  logoUrl: null,
  faviconUrl: null,
  companyName: 'Thaklis'
};

/**
 * The typing center of the currently authenticated session, if any.
 *
 * Hostname resolution is correct before login and for tenants on their own
 * subdomain, but it cannot follow a GROUP_OWNER who opens a typing center via
 * groupSwitch — the acting token changes while the hostname does not. When a
 * session is authenticated against a typing center we therefore re-resolve
 * branding from the authenticated principal.
 */
const getAuthenticatedTypingCenterId = () => {
  try {
    // Portal and group pages never send the staff token, so the staff session stored in the same
    // browser must not drive this lookup there: the call would go out unauthenticated, 401, and the
    // 401 handler would log the portal/group user out.
    const path = window.location.pathname || '';
    if (path.startsWith('/portal') || path.startsWith('/group')) return null;
    if (localStorage.getItem('isAuthenticated') !== 'true') return null;
    if (!localStorage.getItem('jwt_token')) return null;
    const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
    return user?.typingCenterId || null;
  } catch {
    return null;
  }
};

export const TenantProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subdomain, setSubdomain] = useState(null);

  const loadTenantConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const currentSubdomain = getSubdomain();
      setSubdomain(currentSubdomain);

      let config = await fetchTenantConfig(currentSubdomain);

      // An authenticated typing-center session overrides the hostname result.
      // Platform sessions (no typingCenterId) and pre-login loads keep the
      // hostname config untouched. A failed or invalid lookup is non-fatal —
      // the hostname config stands.
      if (getAuthenticatedTypingCenterId()) {
        try {
          const authConfig = await getCurrentTenantConfig();
          if (authConfig?.valid) config = authConfig;
        } catch (authErr) {
          console.warn('Could not resolve tenant config for the authenticated session:', authErr);
        }
      }

      if (config.valid) {
        setTenantConfig(config);
        // Update document title — company name is sourced from tenantName (TC.name)
        if (config.tenantName) {
          document.title = config.tenantName;
        }
        // Update favicon if provided
        if (config.theme?.faviconUrl) {
          updateFavicon(config.theme.faviconUrl);
        }
      } else {
        setError(config.errorMessage || 'Invalid tenant');
        // Set minimal config for error display
        setTenantConfig({
          valid: false,
          theme: defaultTheme,
          errorMessage: config.errorMessage
        });
      }
    } catch (err) {
      console.error('Failed to load tenant config:', err);
      setError('Failed to load tenant configuration');
      setTenantConfig({
        valid: false,
        theme: defaultTheme,
        errorMessage: 'Failed to load configuration'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenantConfig();
  }, [loadTenantConfig]);

  const refreshConfig = useCallback(() => {
    clearTenantCache();
    loadTenantConfig();
  }, [loadTenantConfig]);

  const value = {
    tenantConfig,
    loading,
    error,
    subdomain,
    refreshConfig,
    isPlatform: subdomain === 'doc' || subdomain === 'www' || subdomain === 'app',
    isValid: tenantConfig?.valid ?? false,
    theme: tenantConfig?.theme || defaultTheme,
    tenantId: tenantConfig?.tenantId,
    tenantName: tenantConfig?.tenantName || 'Thaklis',
    tenantCode: tenantConfig?.tenantCode
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Helper function to update favicon
const updateFavicon = (url) => {
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = url;
  document.getElementsByTagName('head')[0].appendChild(link);
};

export default TenantContext;
